import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { AuthShell, Field } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const INIT = { name: "", email: "", password: "", phone: "", city: "", address: "", date_of_birth: "", organization: "",
  designation: "", experience_years: 0, languages: "English, Tamil", bio: "", portfolio_url: "" };

export default function ReporterSignupPage() {
  const [form, setForm] = useState(INIT);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inp = (k, props = {}) => (
    <Input data-testid={`reporter-${k}-input`} value={form[k]} onChange={set(k)} className="rounded-none h-11 bg-white" {...props} />
  );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reporter-signup", { ...form, experience_years: Number(form.experience_years) || 0 });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <AuthShell kicker="Reporter application" title="Application received">
        <div data-testid="reporter-signup-success" className="border border-zinc-300 bg-white p-6">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          <p className="mt-4 text-zinc-700">Our reporter approval team will review your details. You will get an email once a decision is made, and can then sign in to submit stories.</p>
          <Link to="/" className="inline-block mt-6 font-bold underline">Back to the news</Link>
        </div>
      </AuthShell>
    );

  return (
    <AuthShell kicker="Reporter application" title="Report for us" wide>
      <form onSubmit={submit} data-testid="reporter-signup-form" className="space-y-10">
        <fieldset className="grid sm:grid-cols-2 gap-5">
          <legend className="col-span-full font-serif-head text-xl font-bold mb-4 border-b border-zinc-300 pb-2 w-full">Personal details</legend>
          <Field label="Full name *">{inp("name", { required: true })}</Field>
          <Field label="Email *">{inp("email", { type: "email", required: true })}</Field>
          <Field label="Password *" hint="At least 8 characters, with letters and numbers">{inp("password", { type: "password", required: true, minLength: 8 })}</Field>
          <Field label="Phone *">{inp("phone", { required: true })}</Field>
          <Field label="City / Town *">{inp("city", { required: true })}</Field>
          <Field label="Date of birth">{inp("date_of_birth", { type: "date" })}</Field>
          <div className="sm:col-span-2"><Field label="Address">{inp("address")}</Field></div>
        </fieldset>
        <fieldset className="grid sm:grid-cols-2 gap-5">
          <legend className="col-span-full font-serif-head text-xl font-bold mb-4 border-b border-zinc-300 pb-2 w-full">Professional details</legend>
          <Field label="Current organisation">{inp("organization")}</Field>
          <Field label="Designation">{inp("designation")}</Field>
          <Field label="Years of experience">{inp("experience_years", { type: "number", min: 0, max: 60 })}</Field>
          <Field label="Languages you write in">{inp("languages")}</Field>
          <div className="sm:col-span-2"><Field label="Portfolio / sample work link">{inp("portfolio_url", { type: "url", placeholder: "https://" })}</Field></div>
          <div className="sm:col-span-2">
            <Field label="Short bio *" hint="Tell us about your reporting background (min 10 characters)">
              <Textarea data-testid="reporter-bio-input" required minLength={10} value={form.bio} onChange={set("bio")} className="rounded-none bg-white min-h-[120px]" />
            </Field>
          </div>
        </fieldset>
        {error && <p data-testid="reporter-signup-error" className="text-sm text-[#DC2626] border-l-2 border-[#DC2626] pl-3">{error}</p>}
        <Button data-testid="reporter-signup-submit-btn" disabled={busy} className="h-11 px-8 rounded-none font-bold">{busy ? "Submitting…" : "Submit application"}</Button>
      </form>
    </AuthShell>
  );
}
